import { useCallback } from 'react';

const useRCSStructures = () => {
  const createPostbackData = (
    elementType: string,
    elementIndex: number,
    actionIndex?: number,
    cardIndex?: number
  ) => {
    let postback = `{{UUID_MESSAGE}}`;

    switch (elementType) {
      case 'richcard':
        postback += `|card_${elementIndex}|button_${actionIndex}`;
        break;

      case 'carousel':
        postback += `|card_${elementIndex}|carrousel_${cardIndex}|button_${actionIndex}`;
        break;

      case 'contentinfo':
        postback += `|card_${elementIndex}|button_${actionIndex}`;
        break;

      case 'global':
        postback += `|suggestion_${actionIndex}`;
        break;

      default:
        postback += `|unknown`;
        break;
    }

    return postback;
  };

  const createSuggestion = (
    text?: string,
    url?: string,
    gnlLabel?: string,
    startTime?: string,
    endTime?: string,
    title?: string,
    description?: string,
    titleC?: string,
    descriptionC?: string,
    latitude?: string,
    longitude?: string,
    label?: string,
    phoneNumber?: any,
    actionType?: string,
    postbackData?: string
  ) => {
    const action: any = {
      text,
      postbackData: postbackData || '{{UUID_MESSAGE}}'
    };

    if (actionType === 'redirect' && url) {
      action.openUrlAction = { url };
    }

    if (actionType === 'calendar' && startTime && endTime) {
      action.createCalendarEventAction = {
        ...(startTime && { startTime }),
        ...(endTime && { endTime }),
        ...(titleC && { title: titleC }),
        ...(descriptionC && { description: descriptionC })
      };
    }

    if (actionType === 'location' && latitude && longitude) {
      action.viewLocationAction = {
        latLong: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude)
        },
        ...(label && { label })
      };
    }

    if (actionType === 'call' && phoneNumber) {
      action.dialAction = {
        phoneNumber: phoneNumber
      };
    }

    if (text) {
      action.text = text;
      action.postbackData = postbackData || '{{UUID_MESSAGE}}';
    }

    return { action };
  };

  const createCMStructure = useCallback((data: any) => {
    const result: any[] = [];

    data.elements.forEach((element: any, elementIndex: number) => {
      switch (element.type) {
        case 'richCard_Carrosuel': {
          const carouselContent = {
            richCard: {
              carouselCard: {
                cardWidth: 'MEDIUM',
                cardContents: element.cards.map((c: any, cardIndex: number) => {
                  const media = {
                    height: 'MEDIUM',
                    contentInfo: {
                      fileUrl: c.imageCheck ? c.URLImage : c.URLVideo,
                      thumbnailUrl: c.imageCheck
                        ? c.URLImage
                        : c.videoCheck && c.URLImagePoster
                          ? c.URLImagePoster
                          : ''
                    }
                  };

                  let suggestions = [];

                  if (c.actions && c.actions.length > 0) {
                    suggestions = c.actions
                      .map((a: any, actionIndex: number) => {
                        const postbackData = createPostbackData(
                          'carousel',
                          elementIndex,
                          actionIndex,
                          cardIndex
                        );

                        if (a.type === 'answer') {
                          return {
                            reply: {
                              text: a.gnlLabel,
                              postbackData: postbackData
                            }
                          };
                        }

                        const actionData = createSuggestion(
                          a.gnlLabel,
                          a.linkURL,
                          a.gnlLabel,
                          a.startTime,
                          a.endTime,
                          a.titleC,
                          a.descriptionC,
                          a.titleC,
                          a.descriptionC,
                          a.latitude,
                          a.longitude,
                          a.label,
                          a.phoneNumber,
                          a.type,
                          postbackData
                        );

                        return { action: actionData.action };
                      })
                      .filter(Boolean);
                  }
                  return {
                    title: c.title || '',
                    description: c.description || '',
                    media,
                    ...(suggestions.length > 0 ? { suggestions: suggestions } : { suggestions })
                  };
                })
              }
            }
          };
          result.push(carouselContent);
          break;
        }

        case 'richCard': {
          const media = {
            height: 'MEDIUM',
            contentInfo: {
              fileUrl: element.imageCheck ? element.URLImage : element.URLVideo,
              thumbnailUrl: element.imageCheck
                ? element.URLImage
                : element.videoCheck && element.URLImagePoster
                  ? element.URLImagePoster
                  : ''
            }
          };

          let suggestions = [];
          suggestions = (element.actions || [])
            .map((a: any, actionIndex: number) => {
              const postbackData = createPostbackData('richcard', elementIndex, actionIndex);

              if (a.type === 'answer') {
                return {
                  reply: {
                    text: a.gnlLabel,
                    postbackData: postbackData
                  }
                };
              }

              const s = createSuggestion(
                a.gnlLabel,
                a.linkURL,
                a.gnlLabel,
                a.startTime,
                a.endTime,
                a.titleC,
                a.descriptionC,
                a.titleC,
                a.descriptionC,
                a.latitude,
                a.longitude,
                a.label,
                a.phoneNumber,
                a.type,
                postbackData
              );
              return { action: s.action };
            })
            .filter(Boolean);

          const card = {
            richCard: {
              standaloneCard: {
                thumbnailImageAlignment: 'RIGHT',
                cardOrientation: 'VERTICAL',
                cardContent: {
                  title: element.title || '',
                  description: element.description || '',
                  media,
                  ...(suggestions.length > 0 ? { suggestions: suggestions } : { suggestions })
                }
              }
            }
          };
          result.push(card);
          break;
        }

        case 'contenInfo': {
          const media = {
            height: 'MEDIUM',
            contentInfo: {
              fileUrl: element.fileCheck
                ? element.URLFile
                : element.imageCheck
                  ? element.URLImage
                  : element.videoCheck
                    ? element.URLVideo
                    : '',
              thumbnailUrl: element.imageCheck ? element.URLImage : element.URLImagePoster,
              forceRefresh: element.forceRefresh || false
            }
          };

          const contentInfo = {
            suggestions: [],
            contentInfo: {
              fileUrl: media.contentInfo.fileUrl,
              thumbnailUrl: media.contentInfo.thumbnailUrl,
              forceRefresh: media.contentInfo.forceRefresh
            }
          };
          result.push(contentInfo);
          break;
        }

        default:
          break;
      }
    });

    if (data.suggestions && data.suggestions.length > 0) {
      const globalSuggestions = (data.suggestions || [])
        .map((a: any, suggestionIndex: number) => {
          const postbackData = createPostbackData('global', 0, suggestionIndex);

          if (a.type === 'answer') {
            return {
              reply: {
                text: a.gnlLabel,
                postbackData: postbackData
              }
            };
          } else {
            const suggestionData = createSuggestion(
              a.gnlLabel,
              a.linkURL,
              a.gnlLabel,
              a.startTime,
              a.endTime,
              a.titleC,
              a.descriptionC,
              a.titleC,
              a.descriptionC,
              a.latitude,
              a.longitude,
              a.label,
              a.phoneNumber,
              a.type,
              postbackData
            );
            return { action: suggestionData.action };
          }
        })
        .filter(Boolean);

      const last = result[result.length - 1];
      if (last?.richCard?.standaloneCard || last?.richCard?.carouselCard) {
        last.suggestions = globalSuggestions;
      } else if (last?.contentInfo) {
        last.suggestions = globalSuggestions;
      }
    }

    return result;
  }, []);

  return { createCMStructure };
};

export default useRCSStructures;
